import React from 'react';
import { LuCheck, LuLoader2, LuX } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import type { CloseButton } from 'react-toastify';

import Button from 'components/common/Button';
import { COPY_TOOL_COPY_RUNNING_REASON } from 'constants/messageConstants';
import {
  COPY_TOOL_CLOSE_TOAST_LABEL,
  COPY_TOOL_COPY_DONE_STATUS,
  COPY_TOOL_COPY_FAILED_STATUS,
  COPY_TOOL_COPY_PENDING_STATUS,
  COPY_TOOL_RETRY_LABEL,
} from 'constants/uiConstants';
import { ICopyTarget } from 'interfaces/ICopyPaddockData';

import { getIsCopyRunning } from '../../redux/selectors/CopyToolSelectors';

export enum CopyRowStatus {
  Pending = 'pending',
  Copied = 'copied',
  Failed = 'failed',
}

export interface CopyProgressRow {
  target: ICopyTarget;
  label: string;
  status: CopyRowStatus;
}

interface ICopyProgressToastProps {
  title: string;
  rows: CopyProgressRow[];
  /** Shown once the copy has settled with failures; copies those again. */
  onRetry?: () => void;
}

const STATUS_TEXT: Record<CopyRowStatus, string> = {
  [CopyRowStatus.Pending]: COPY_TOOL_COPY_PENDING_STATUS,
  [CopyRowStatus.Copied]: COPY_TOOL_COPY_DONE_STATUS,
  [CopyRowStatus.Failed]: COPY_TOOL_COPY_FAILED_STATUS,
};

const StatusIcon: React.FC<{ status: CopyRowStatus }> = ({ status }) => {
  if (status === CopyRowStatus.Copied)
    return <LuCheck className="flex-shrink-0 text-success" aria-hidden />;
  if (status === CopyRowStatus.Failed)
    return <LuX className="flex-shrink-0 text-danger" aria-hidden />;
  return (
    <LuLoader2
      className="flex-shrink-0 animate-spin text-gray-dark"
      aria-hidden
    />
  );
};

/** One toast for a whole copy: its progress, each document's outcome and a retry of the failed ones. */
const CopyProgressToast: React.FC<ICopyProgressToastProps> = ({
  title,
  rows,
  onRetry,
}) => {
  // One copy at a time, so Retry waits for any other copy to finish.
  const isCopyRunning = useSelector(getIsCopyRunning);
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="font-semibold text-charcoal">{title}</p>
      {/* About six rows, then it scrolls, so a large copy doesn't fill the screen. */}
      <ul className="flex max-h-40 flex-col gap-1 overflow-y-auto">
        {rows.map((row) => (
          <li
            key={`${row.target.paddockId}:${row.target.inputId}`}
            className="flex items-center gap-2 text-gray-dark"
          >
            <StatusIcon status={row.status} />
            <span className="truncate">{row.label}</span>
            <span className="sr-only">{STATUS_TEXT[row.status]}</span>
          </li>
        ))}
      </ul>
      {onRetry && (
        <Button
          variant="secondary"
          className="self-start px-3 py-1 text-sm font-semibold"
          onClick={onRetry}
          disabled={isCopyRunning}
          title={isCopyRunning ? COPY_TOOL_COPY_RUNNING_REASON : undefined}
          type="button"
        >
          {COPY_TOOL_RETRY_LABEL}
        </Button>
      )}
    </div>
  );
};

export default CopyProgressToast;

/** The toast's content, for `toast.loading` and `toast.update`. */
export const renderCopyProgress = (
  title: string,
  rows: CopyProgressRow[],
  onRetry?: () => void
) => (
  // A copy, so each update renders the rows as they are at that moment.
  <CopyProgressToast
    title={title}
    rows={rows.map((row) => ({ ...row }))}
    onRetry={onRetry}
  />
);

/**
 * The toast's close button, which says when it's clicked: a closing toast
 * still counts as active while it animates out, so the copy can't tell from
 * react-toastify alone. Styled with react-toastify's own classes.
 */
export const renderCloseButton = (onClose: () => void) => {
  const CopyToastCloseButton = ({
    closeToast,
    theme,
  }: Parameters<typeof CloseButton>[0]) => (
    <button
      type="button"
      className={`Toastify__close-button Toastify__close-button--${theme}`}
      aria-label={COPY_TOOL_CLOSE_TOAST_LABEL}
      onClick={(event) => {
        event.stopPropagation();
        onClose();
        closeToast(event);
      }}
    >
      <LuX aria-hidden />
    </button>
  );
  return CopyToastCloseButton;
};
